import React, { useMemo } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { type ConnectionInfo } from './gateway-modal'
import GatewayConnectionStream from './gateway-connection-stream'
import { inferExchangeSymbol } from '../../../../utils/exchange-utils'

interface GatewayConnectionGridProps {
  connectionInfo: ConnectionInfo
  reconnectCallback: (identifier: string, symbol: string | null) => void
}

const GatewayConnectionItem: React.FC<GatewayConnectionGridProps> = ({ connectionInfo, reconnectCallback }) => {
  const { identifier, name, states, parameters } = connectionInfo

  const children = useMemo<React.ReactNode[]>(() => {
    return connectionInfo.connections
      .sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }))
      .map((connection) => {
        const symbol = inferExchangeSymbol(identifier, connection.params, connection.uri)
        const callbackAction = (): void => {
          reconnectCallback(identifier, symbol)
        }
        return (
          <GatewayConnectionStream
            key={connection.id}
            identifier={identifier}
            connection={connection}
            reconnectCallback={callbackAction}
          />
        )
      })
  }, [identifier, connectionInfo.connections])

  return (
    <Grid item xs={12} md={6} lg={3}>
      <Typography color='primary' fontSize='large' fontWeight='bold'>{name}</Typography>

      <Accordion>
        <AccordionSummary expandIcon={<ArrowDropDownIcon/>}>
          <Typography>States</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography sx={{ whiteSpace: 'break-spaces' }}>
            {JSON.stringify(states, null, 2)}
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ArrowDropDownIcon/>}>
          <Typography>Parameters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography sx={{ whiteSpace: 'break-spaces' }}>
            {JSON.stringify(parameters, null, 2)}
          </Typography>
        </AccordionDetails>
      </Accordion>

      {children}
    </Grid>
  )
}

export default React.memo(GatewayConnectionItem)
